import { Box, Button, DialogContent, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import React from 'react';

import { DialogChromeFooter, dialogPrimaryActionSx } from 'components/atoms/Dialog/DialogChrome';
import { Study } from 'interfaces/study';

interface ExportSectionProps {
  number: number;
  title: string;
  children: React.ReactNode;
}

const ExportSection: React.FC<ExportSectionProps> = ({ number, title, children }) => (
  <Box component="section" aria-labelledby={`results-export-section-${number}`} sx={{ mb: 3 }}>
    <Typography id={`results-export-section-${number}`} component="h3" variant="subtitle1" sx={{ mb: 1 }}>
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const ExportDestinationSection: React.FC<{ study?: Study }> = ({ study }) => {
  const bucketName = study?.settings?.exportBucket?.bucketName;
  return (
    <>
      <RadioGroup aria-label="Destination" value="download">
        <FormControlLabel value="download" control={<Radio />} label="Download" />
        <FormControlLabel value="bucket" control={<Radio />} disabled label="S3 bucket" />
      </RadioGroup>
      <Typography variant="body2" color="text.secondary">
        {bucketName
          ? `Delivery to ${bucketName} is not available for this export yet. Download is available.`
          : 'No export bucket is configured in study settings. Download is available.'}
      </Typography>
    </>
  );
};

interface ExportFormShellProps {
  study?: Study;
  scope: React.ReactNode;
  // Hosts that choose what to export from a list place it between the scope and the options.
  selection?: React.ReactNode;
  selectionTitle?: string;
  options: React.ReactNode;
  status?: React.ReactNode;
  primaryLabel: string;
  primaryDisabled?: boolean;
  // Neutral guidance shown beside a blocked primary action.
  primaryGuidance?: string;
  onPrimary: () => void;
  onBack: () => void;
  backLabel?: string;
  // Hosts that only offer a browser download omit the Destination section.
  downloadOnly?: boolean;
  optionsTitle?: string;
}

// Scope, Export options and Destination in the approved order; type forms supply only their own controls.
export const ExportFormShell: React.FC<ExportFormShellProps> = ({
  study,
  scope,
  selection,
  selectionTitle = 'Results to export',
  options,
  status,
  primaryLabel,
  primaryDisabled = false,
  primaryGuidance,
  onPrimary,
  onBack,
  backLabel = 'Back',
  downloadOnly = false,
  optionsTitle = 'Export options',
}) => (
  <>
    <DialogContent dividers>
      <ExportSection number={1} title="Scope">
        {scope}
      </ExportSection>
      {selection && (
        <ExportSection number={2} title={selectionTitle}>
          {selection}
        </ExportSection>
      )}
      <ExportSection number={selection ? 3 : 2} title={optionsTitle}>
        {options}
      </ExportSection>
      {!downloadOnly && (
        <ExportSection number={selection ? 4 : 3} title="Destination">
          <ExportDestinationSection study={study} />
        </ExportSection>
      )}
      {status}
    </DialogContent>
    <DialogChromeFooter>
      <Typography variant="body2" color="text.secondary" role="status" sx={{ mr: 'auto' }}>
        {primaryDisabled ? primaryGuidance : ''}
      </Typography>
      <Button color="inherit" onClick={onBack}>
        {backLabel}
      </Button>
      <Button
        variant="contained"
        color="primary"
        disabled={primaryDisabled}
        onClick={onPrimary}
        sx={dialogPrimaryActionSx}
      >
        {primaryLabel}
      </Button>
    </DialogChromeFooter>
  </>
);
